import React, { useState } from 'react';
import {
    TeamOutlined,
    ArrowRightOutlined,
    UserAddOutlined,
    BankOutlined,
    EuroCircleOutlined,
    AreaChartOutlined,
    UnorderedListOutlined,
    DeleteRowOutlined,
    EditOutlined,
    DownOutlined,
    UpOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
    Button,
    Space,
    Card,
    Typography,
    Tooltip,
    Avatar,
    Divider,
    Skeleton,
    Tag,
    Image,
    Checkbox,
    Col, Row,
    List,
    Tabs,
} from 'antd';

import { useSessionStorage } from '../../models/useSessionStorage';
import { useTemplatePartners } from '../../models/useTemplatePartners';
import { useTeamMemberIds } from '../../models/useTeamMemberIds';
import { RestrictedTeamPermissions } from '../../utils/Constants';
import StrikeTeamDetailsModel from '../../models/StrikeTeamDetailsModel';
import MoneyTitle from '../customer/MoneyTitle';

import { TeamMemberAvatarForRole, IconForCCY, TeamMemberTitleForItem } from './TeamDashboardComponents';
import { ConnectedPartnerBadge, ConnectedPartnerAdminLine } from './ConnectedPartnerBadge';
import { useClientBalances } from './useClientBalances';
import { TeamMemberDeleteButton } from './TeamMemberDeleteButton';
import { TeamMemberLeaveButton } from './TeamMemberLeaveButton';
import '../../App.scss';
import 'antd/dist/antd.css';
import { useClientTeams } from './useClientTeams';

const { Text } = Typography;

const restrictedPermissions = {
    ...RestrictedTeamPermissions
};

const safelyMapPermission = (permissions) => {
    const {
        canAddMembers,
        canPayMembers,
        canSeeAllTxns,
        canSeeMembers,
        canSeePayouts,
        canSeeTeamBalance,
        isTeamAdmin,
    } = permissions || restrictedPermissions;

    return {
        canAddMembers,
        canPayMembers,
        canSeeAllTxns,
        canSeeMembers,
        canSeePayouts,
        canSeeTeamBalance,
        isTeamAdmin,
    };
};

export const TeamProfileBalancesCard = ({ locStrings, condition, teamClient, teamClientId, teamId, permissions, team, ...rest }) => {
    const teamClientBalances = useClientBalances(teamClientId);

    const {
        canAddMembers,
        canPayMembers,
        canSeeAllTxns,
        canSeeMembers,
        canSeePayouts,
        canSeeTeamBalance,
        isTeamAdmin,
    } = safelyMapPermission(permissions);

    // Check if pooling is enabled or if team has balance > 0
    const isPoolingEnabled = team && team.teamTapBehaviour && (
        team.teamTapBehaviour === 'team' || 
        team.teamTapBehaviour === 'both'
    );
    
    const hasTeamBalance = teamClientBalances && teamClientBalances.some(account => account.available > 0);
    
    const shouldShowBalanceButtons = isPoolingEnabled || hasTeamBalance;
    
    // Debug logging removed

    return teamClientBalances ? (
        <>
            {(isTeamAdmin || canSeeTeamBalance || canPayMembers) && shouldShowBalanceButtons &&
                teamClientBalances.map((account, i) => {
                    return (
                        <Row key={`bal-row-${teamId}-${account.availableAccountId}`} gutter={[{ xs: 4, sm: 6, md: 10, lg: 32 }, { xs: 2, sm: 3, md: 6, lg: 8 }]}> 
                            <ActionButtonCol key={`abc-${teamId}-${account.availableAccountId}-bal`}>
                                <TeamProfileTeamMembersAction
                                    route={canPayMembers ? `./${teamId}/payout/${account.availableAccountId}` : '#'}
                                    text={<Space direction='horizontal'>
                                        {'Balance'}
                                        <MoneyTitle
                                            amount={account.available}
                                            currency={account.currency}
                                            digits={2}
                                        />
                                    </Space>}
                                    icon={<IconForCCY ccy={account.currency} />}
                                    style={{ width: '100%' }}
                                    arrow={canPayMembers}
                                />
                            </ActionButtonCol>
                            <ActionButtonCol key={`abc-${teamId}-${account.availableAccountId}-payout`}>
                                <TeamProfileTeamMembersAction
                                    route={canPayMembers ? `./${teamId}/payout/${account.availableAccountId}` : '#'}
                                    text={'Team Member Payouts'}
                                    icon={<IconForCCY ccy={account.currency} />}
                                    style={{ width: '100%' }}
                                    arrow={canPayMembers}
                                />
                            </ActionButtonCol>
                        </Row>
                    )
                })
            }
        </>
    ) : <Skeleton button size='large' />;
};


export const TeamProfileTeamMembersAction = ({ route, text, children, icon: ActionIcon, arrow, border, style, ...rest }) => {
    const hasArrow = arrow == null ? true : arrow;
    const bordered = border == null ? true : !!border;

    return (
        <Card type='inner' size='small' bodyStyle={{ paddingLeft: '0px' }} bordered={bordered}>
            <Link to={route} style={{ color: '#000' }}>
                <Row align='middle'>
                    <Col span={2}></Col>
                    <Col span={2}><div style={{ color: '#ff0098', ...style }} {...rest}>{ActionIcon}</div></Col>
                    <Col span={1}></Col>
                    <Col span={17}><Text ellipsis >{text}</Text></Col>
                    <Col span={2}>{hasArrow && <ArrowRightOutlined />}</Col>
                </Row>
            </Link>
        </Card>
    );
};


const ShowOnTapCheckBox = ({ show, member, onChange, style, ...rest }) => {

    const { memberClientId, teamId } = member;

    const isTippable = show;

    const onCheckTapped = async (e) => {
        const newIsTippableState = e.target.checked;
        // setIsTippable(newIsTippableState);
        onChange && onChange(newIsTippableState);
    };

    // Allow style override, but default to no margin for grid layout
    const checkboxStyle = style || { margin: 0, whiteSpace: 'nowrap' };

    return <Checkbox
        checked={isTippable}
        onChange={onCheckTapped}
        {...rest}
        style={checkboxStyle}
    >Show member</Checkbox>
};


const TeamMemberDescription = ({ item, ...rest }) => {
    return (
        <Space direction='horizontal' size='small' key={item.key}>
            { item.permissionIcons.length > 0 &&
                item.permissionIcons.map(
                    (icon, i) => {
                        return <Space key={i}>{icon}</Space>;
                    }
                )
            }
            { item.permissionIcons.length < 1 &&
                <>{item.showOnTap ? 'Can be tipped' : 'Not available to tip'}</>
            }
        </Space>
    );
};


export const ActionButtonCol = ({ children, ...rest }) => {
    return <Col xs={24} sm={12} md={8} lg={8} xl={6} xxl={4}>{children}</Col>

};

export const TeamMemberPermissionIcon = ({ allowed, labels, icons, ...rest }) => {
    const isAllowed = !!allowed;

    const style = { fontSize: '18px', color: isAllowed ? 'green' : 'red' };

    const AllowedIcon = ({ icon: Icon, ...rest }) => {
        return <Icon {...rest} />;
    };

    return (
        isAllowed ?
            <Tooltip title={labels[0]} key={labels[0]}>
                <AllowedIcon icon={icons[0]} style={style} />
            </Tooltip>
            :
            <Tooltip title={labels[1]} key={labels[1]}>
                <AllowedIcon icon={icons[1]} style={style} />
            </Tooltip>
    );
};


function getRoleForMember({ team, member }) {
    const role = (team.teamOwnerClientId === member.clientId)
        ?
        'owner'
        :
        (member.permissions && member.permissions.isTeamAdmin ? 'manager' : member.roles[0]);

    return role;
}


export const TeamMembersGrid = ({ condition, locStrings, team, teamId, members, clientId, limit, displayPermissions, isInviteVisible, teamTapBehaviour, onChange, isAdmin, ...rest }) => {
    const [activeFilter, setActiveFilter] = useState('all');
    const [isCheckboxWaiting, setIsCheckboxWaiting] = useState(false);
    
    // Session storage key based on teamId to remember expanded state per team
    const sessionStorageKey = `team-members-expanded-${teamId}`;
    const [isExpanded, setIsExpanded] = useSessionStorage(sessionStorageKey, false);
    
    const INITIAL_MEMBERS_TO_SHOW = 9; // 3x3 grid on large screens

    const updateShowOnTap = async (v, memberRow) => {
        const model = new StrikeTeamDetailsModel({
            teamId,
            memberClientId: memberRow.memberClientId,
        });
        await model.setShowMemberOnTapScreenFlag(v, memberRow);
    };

    const backgroundColorByRole = {
        admin: 'magenta',
        member: 'green',
        manager: 'blue',
        owner: 'navy',
        default: 'black'
    };

    // Process members: get role, create member data, sort alphabetically
    // Include deleted members if admin
    const allMembers = (members || []).filter(m => isAdmin || !m.isDeleted);

    // Partner-integration indicators: a non-empty externalIds map on a member doc
    // means the member is connected to a partner integration. Resolve each
    // templateTagId key -> partner branding once per page (cached in the hook).
    const externalTemplateTagIds = allMembers.flatMap(m => Object.keys(m.externalIds || {}));
    const partnersByTemplate = useTemplatePartners(externalTemplateTagIds);
    // Strike Admin only: the memberIds binding docs, for per-bind tagUid detail.
    
    const processedMembers = allMembers
        .map(member => {
            const isYourself = member.clientId === clientId;
            const role = getRoleForMember({ team, member });
            const displayName = isYourself ? `${member.displayName} (You)` : member.displayName;
            const routeMember = displayPermissions && displayPermissions.isTeamAdmin 
                ? `./${teamId}/members/${member.clientId}` 
                : null;

            // Get permission icons for this member
            const teamMemberPermissions = safelyMapPermission(member.permissions);
            const permissionIcons = getPermissionIcons(displayPermissions, teamMemberPermissions);

            return {
                ...member,
                displayName,
                role,
                memberColor: backgroundColorByRole[role] || backgroundColorByRole.default,
                isYourself,
                routeMember,
                memberClientId: member.clientId, // Ensure memberClientId is set for TeamMemberDeleteButton
                teamId: teamId, // Ensure teamId is set for TeamMemberDeleteButton
                permissionIcons, // Add permission icons
            };
        })
        .sort((a, b) => {
            // Sort alphabetically by display name (case-insensitive)
            return a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' });
        });

    // Filter members based on active filter
    const filteredMembers = processedMembers.filter(member => {
        if (activeFilter === 'all') return !member.isDeleted;
        if (activeFilter === 'owner') return member.role === 'owner' && !member.isDeleted;
        if (activeFilter === 'managers') return member.role === 'manager' && !member.isDeleted;
        if (activeFilter === 'members') return member.role === 'member' && !member.isDeleted;
        if (activeFilter === 'deleted') return member.isDeleted === true;
        return true;
    });

    const capitalize = (str) => {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const renderMemberCard = (member) => {
        const teamName = team && team.teamName ? team.teamName : '';
        const limitDelete = ['owner', 'manager'];
        // Delete button: show for team admins, not yourself, not owner/manager, and not deleted
        const canDelete = displayPermissions && displayPermissions.isTeamAdmin && !member.isYourself && !limitDelete.includes(member.role) && !member.isDeleted;
        // Restore button: handled by TeamMemberDeleteButton component itself (checks isAdmin internally)
        const canRestore = isAdmin && member.isDeleted;

        const behavoursForEnabledShowCheckbox = ['both', 'members'];
        const showCheckboxDisabled = !behavoursForEnabledShowCheckbox.includes(teamTapBehaviour);
        const showCheckbox = displayPermissions && displayPermissions.isTeamAdmin;
        
        // Check if current user is a manager or owner (can see Staff ID)
        const currentUserMember = members && members.find(m => m.clientId === clientId);
        const currentUserRole = currentUserMember ? getRoleForMember({ team, member: currentUserMember }) : null;
        const canSeeStaffId = currentUserRole === 'owner' || currentUserRole === 'manager';
        
        const cardContent = (
            <Card
                hoverable={false}
                style={{ 
                    opacity: member.isDeleted ? 0.6 : 1,
                    position: 'relative',
                    minWidth: '280px',
                    width: '100%'
                }}
                bodyStyle={{ padding: '8px' }}
            >
                {/* Delete/Restore Button - Top Right */}
                {(canDelete || canRestore) && (
                    <div style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 10 }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ 
                            backgroundColor: 'white', 
                            borderRadius: '50%', 
                            width: '24px', 
                            height: '24px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                        }}>
                            <TeamMemberDeleteButton 
                                member={member} 
                                isAdmin={isAdmin}
                                teamName={teamName}
                                iconOnly={true}
                            />
                        </div>
                    </div>
                )}
                <div style={{ width: '100%' }}>
                    {/* Avatar, Name, and Membership Section */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', marginBottom: '4px' }}>
                        {/* Avatar container */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: '64px' }}>
                            <style>
                                {`
                                    .avatar-container-wrapper,
                                    .avatar-container-wrapper > *,
                                    .avatar-container-wrapper a,
                                    .avatar-container-wrapper button,
                                    .avatar-container-wrapper button.ant-btn-link {
                                        margin: 0 !important;
                                        padding: 0 !important;
                                        display: flex !important;
                                        align-items: center !important;
                                        justify-content: center !important;
                                        width: 100% !important;
                                        height: 100% !important;
                                    }
                                `}
                            </style>
                            <div className="avatar-container-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: 0, padding: 0, width: '100%', height: '100%' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: 0, padding: 0 }}>
                                    <TeamMemberAvatarForRole 
                                        member={member} 
                                        team={team} 
                                        route={null}
                                        size="large" 
                                    />
                                </div>
                            </div>
                            {/* Permission icons under avatar */}
                            {member.permissionIcons && member.permissionIcons.length > 0 && (
                                <div style={{ display: 'flex', gap: '4px', marginTop: '4px', justifyContent: 'center' }}>
                                    {member.permissionIcons}
                                </div>
                            )}
                        </div>
                        {/* Name and role container */}
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: '0px' }}>
                            {member.routeMember && !member.isDeleted ? (
                                <Text 
                                    strong={member.isYourself} 
                                    style={{ 
                                        margin: 0, 
                                        lineHeight: '1.2',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        wordBreak: 'break-word'
                                    }}
                                >
                                    <Link 
                                        to={member.routeMember} 
                                        style={{ 
                                            textDecoration: 'none', 
                                            color: '#1890ff',
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.textDecoration = 'underline';
                                            e.currentTarget.style.color = '#40a9ff';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.textDecoration = 'none';
                                            e.currentTarget.style.color = '#1890ff';
                                        }}
                                    >
                                        {member.displayName}
                                        <ArrowRightOutlined style={{ fontSize: '12px', opacity: 0.7 }} />
                                    </Link>
                                </Text>
                            ) : (
                                <Text 
                                    strong={member.isYourself} 
                                    style={{ 
                                        margin: 0, 
                                        lineHeight: '1.2',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        wordBreak: 'break-word'
                                    }}
                                >
                                    {member.displayName}
                                </Text>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '2px' }}>
                                <Tag color={member.memberColor}>
                                    {capitalize(member.role)}
                                </Tag>
                                {member.isDeleted && (
                                    <Tag color="red">
                                        Deleted
                                    </Tag>
                                )}
                                {/* Corner-check badge only — admins get the xmid appended
                                    to the rollover; full bind detail lives on the member
                                    detail page. */}
                                {Object.entries(member.externalIds || {}).map(([templateTagId, xmid]) => (
                                    <ConnectedPartnerBadge
                                        key={`connected-${member.clientId}-${templateTagId}`}
                                        partner={partnersByTemplate[templateTagId]}
                                        xmid={isAdmin ? xmid : null}
                                    />
                                ))}
                            </div>
                            {canSeeStaffId && (
                                <>
                                    {member.staffId ? (
                                        <Text type="secondary" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                            Staff ID: {member.staffId}
                                        </Text>
                                    ) : member.routeMember && !member.isDeleted ? (
                                        <Link 
                                            to={member.routeMember}
                                            style={{ 
                                                textDecoration: 'none',
                                                display: 'inline-block',
                                                marginTop: '4px'
                                            }}
                                        >
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<PlusOutlined />}
                                                style={{
                                                    padding: 0,
                                                    height: 'auto',
                                                    fontSize: '12px',
                                                    color: '#1890ff'
                                                }}
                                            >
                                                Add Staff ID
                                            </Button>
                                        </Link>
                                    ) : null}
                                </>
                            )}
                        </div>
                    </div>
                    
                    {/* Show Member Checkbox - Left Aligned */}
                    {showCheckbox && (
                        <div style={{ marginTop: '4px', marginLeft: '76px' }} onClick={(e) => e.stopPropagation()}>
                            <ShowOnTapCheckBox
                                disabled={showCheckboxDisabled || isCheckboxWaiting}
                                show={member.showOnTap}
                                member={member}
                                onChange={(v) => updateShowOnTap(v, member)}
                                style={{ margin: 0, whiteSpace: 'nowrap' }}
                            />
                        </div>
                    )}
                </div>
            </Card>
        );

        return cardContent;
    };

    const activeMembers = processedMembers.filter(m => !m.isDeleted);
    const deletedMembers = processedMembers.filter(m => m.isDeleted);
    const managersAndOwners = activeMembers.filter(m => m.role === 'owner' || m.role === 'manager');

    const staffCount = activeMembers.filter(m => m.role === 'member').length;
    const adminsCount = managersAndOwners.length;
    const allCount = activeMembers.length;
    // Always create filter tabs - they should always be visible
    const filterTabs = [
        { key: 'all', label: `All (${allCount})` },
        { key: 'members', label: `Staff (${staffCount})` },
        { key: 'managers', label: `Admins (${adminsCount})` },
    ];
    
    // Add deleted filter for admin - always show if admin, even if count is 0
    if (isAdmin) {
        filterTabs.push({ key: 'deleted', label: `Deleted (${deletedMembers.length})` });
    }
    
    // Ensure we always have tabs to display
    if (!filterTabs || filterTabs.length === 0) {
        filterTabs.push({ key: 'all', label: 'All (0)' });
    }

    return (
        <>

            <Tabs
                activeKey={activeFilter}
                onChange={setActiveFilter}
                style={{ marginBottom: '16px' }}
            >
                {filterTabs.map(tab => {
                    // Filter members for this specific tab
                    const tabFilteredMembers = processedMembers.filter(member => {
                        if (tab.key === 'all') return !member.isDeleted;
                        if (tab.key === 'managers') return (member.role === 'manager' || member.role === 'owner') && !member.isDeleted;
                        if (tab.key === 'members') return member.role === 'member' && !member.isDeleted;
                        if (tab.key === 'deleted') return member.isDeleted === true;
                        return true;
                    });

                    // Determine which members to show for this tab based on expanded state
                    const tabMembersToShow = isExpanded ? tabFilteredMembers : tabFilteredMembers.slice(0, INITIAL_MEMBERS_TO_SHOW);
                    const tabHasMoreMembers = tabFilteredMembers.length > INITIAL_MEMBERS_TO_SHOW;

                    return (
                        <Tabs.TabPane tab={tab.label} key={tab.key}>
                            {tabFilteredMembers.length > 0 ? (
                                <>
                                    <Row gutter={[16, 16]}>
                                        {tabMembersToShow.map((member) => (
                                            <Col 
                                                key={member.clientId}
                                                xs={24}
                                                sm={12}
                                                md={10}
                                                lg={8}
                                                xl={8}
                                                xxl={6}
                                            >
                                                {renderMemberCard(member)}
                                            </Col>
                                        ))}
                                    </Row>
                                    {tabHasMoreMembers && (
                                        <div style={{ textAlign: 'center', marginTop: '16px' }}>
                                            <Button
                                                type="link"
                                                icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
                                                onClick={() => setIsExpanded(!isExpanded)}
                                            >
                                                {isExpanded ? 'Show less' : `Show more (${tabFilteredMembers.length - INITIAL_MEMBERS_TO_SHOW} more)`}
                                            </Button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                    No members found
                                </div>
                            )}
                        </Tabs.TabPane>
                    );
                })}
            </Tabs>
        </>
    );
};


function getPermissionIcons(displayPermissions, teamMemberPermissions) {
    let permissionIcons = [];

    if (displayPermissions && displayPermissions.isTeamAdmin) {
        // People icon: See members OR (see and add members)
        const canSeeMembers = teamMemberPermissions.canSeeMembers;
        const canAddMembers = teamMemberPermissions.canAddMembers;
        let peopleColor = 'red'; // Cannot see members
        let peopleLabel = 'Cannot See Members';
        
        if (canSeeMembers && canAddMembers) {
            peopleColor = 'green'; // Can see and add members
            peopleLabel = 'Can See and Add Members';
        } else if (canSeeMembers) {
            peopleColor = '#faad14'; // Orange/yellow - can see members only
            peopleLabel = 'Can See Members';
        }
        
        permissionIcons.push(
            <Tooltip key="people-permission" title={peopleLabel}>
                <TeamOutlined style={{ fontSize: '18px', color: peopleColor }} />
            </Tooltip>
        );

        // Money icon: See balances OR (see balance and transaction) OR (see balance and transaction and payout)
        const canSeeBalance = teamMemberPermissions.canSeeTeamBalance;
        const canSeeTxns = teamMemberPermissions.canSeeAllTxns;
        const canSeePayouts = teamMemberPermissions.canSeePayouts;
        let moneyColor = 'red'; // Cannot see balance
        let moneyLabel = 'Cannot See Balance';
        
        if (canSeeBalance && canSeeTxns && canSeePayouts) {
            moneyColor = 'green'; // Full access: balance, transactions, and payouts
            moneyLabel = 'Can See Balance, Transactions, and Payouts';
        } else if (canSeeBalance && canSeeTxns) {
            moneyColor = '#faad14'; // Orange/yellow - can see balance and transactions
            moneyLabel = 'Can See Balance and Transactions';
        } else if (canSeeBalance) {
            moneyColor = '#52c41a'; // Light green - can see balance only
            moneyLabel = 'Can See Balance';
        }
        
        permissionIcons.push(
            <Tooltip key="money-permission" title={moneyLabel}>
                <EuroCircleOutlined style={{ fontSize: '18px', color: moneyColor }} />
            </Tooltip>
        );
    }
    return permissionIcons;
};

export const TeamLeaveTeamCard = ({ locStrings, clientId, team, permissions, isAdmin, ...rest }) => {
    const { teamName, teamNumberOfMembers = 0, teamOwnerClientId } = team;
    const {
        canAddMembers,
        canPayMembers,
        canSeeAllTxns,
        canSeeMembers,
        canSeePayouts,
        canSeeTeamBalance,
        isTeamAdmin,
    } =  safelyMapPermission(permissions);

    const isOwner = clientId === teamOwnerClientId;
    const canShowLeave = (isOwner && teamNumberOfMembers === 1) || !isTeamAdmin;

    return true ? (
        <>
            {canShowLeave && clientId && team &&
            (
                <Row key={`team-leave-row-${clientId}`} gutter={[{ xs: 4, sm: 6, md: 10, lg: 32 }, { xs: 2, sm: 3, md: 6, lg: 8 }]}> 
                    <ActionButtonCol key={`leave-${clientId}-button`}>
                        <TeamMemberLeaveButton 
                            clientId={clientId}
                            team={team}
                            isAdmin={isAdmin}
                        />
                    </ActionButtonCol>
                </Row>
            )
            }
        </>
    ) : <Skeleton button size='large' />;
};
